import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "z-[120] fixed bg-white/90 w-screen h-screen flex items-center justify-center"
}
const _hoisted_2 = { class: "" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "loader" }, null, -1)
const _hoisted_4 = { class: "mt-16 text-base font-semibold" }

import { computed, onMounted, watchEffect } from "vue";
import { toastContainers } from "vue3-toastify";
import { startRegionMovedWatch } from "./lib/region-moved";
import { loadOnSignIn } from "./lib/reference-data";
import { auth } from "./utils/firebase";
import { useDataStore } from "./stores/dataStore";
import { useSubjectStore } from "./stores/subjectStore";
import { useProductStore } from "./stores/productStore";
import { useSchoolStore } from "./stores/schoolStore";
import { useFilterStore } from "./stores/filterStore";
import { useUserStore } from "./stores/userStore";
import { useSchoolGroupStore } from "./stores/schoolGroupsStore";
import OutageBannerComponent from "./components/global/OutageBannerComponent.vue";
import {
  identifyFreshchatUser,
  onFreshchatRestoreId,
} from "./utils/freshchat";

type FreshchatConfig = {
  value: boolean | undefined;
  mode: "school" | "group" | undefined;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const dataStore = useDataStore();
const subjectStore = useSubjectStore();
const productStore = useProductStore();
const schoolStore = useSchoolStore();
const filterStore = useFilterStore();
const userStore = useUserStore();
const schoolGroupStore = useSchoolGroupStore();

onFreshchatRestoreId(async (restoreId: string) => {
  await userStore.syncFreshchatRestoreId(restoreId);
});

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const normalizeBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (["true", "1", "yes", "enabled"].includes(normalized)) {
      return true;
    }

    if (["false", "0", "no", "disabled"].includes(normalized)) {
      return false;
    }
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }

    if (value === 0) {
      return false;
    }
  }

  return undefined;
};

const getFreshchatConfig = (source: unknown): FreshchatConfig => {
  if (!isRecord(source)) {
    return { value: undefined, mode: undefined };
  }

  const candidate = source as {
    settings?: unknown;
    freshchat?: unknown;
    freshchatMode?: unknown;
  };

  const settingsRecord = isRecord(candidate.settings) ? candidate.settings : undefined;
  const rawMode = settingsRecord?.freshchatMode ?? candidate.freshchatMode;
  const rawValue = settingsRecord?.freshchat ?? candidate.freshchat;

  const normalizedMode = typeof rawMode === "string" ? rawMode.toLowerCase() : undefined;
  const normalizedValue = normalizeBoolean(rawValue);

  if (normalizedMode === "school") {
    return { mode: "school", value: normalizedValue ?? false };
  }

  if (normalizedMode === "group") {
    return { mode: "group", value: normalizedValue };
  }

  return { mode: undefined, value: normalizedValue };
};

const getSchoolGroupId = (source: unknown): string | undefined => {
  if (!isRecord(source)) {
    return undefined;
  }

  const candidate = source as { schoolGroup?: unknown };
  const { schoolGroup } = candidate;

  if (typeof schoolGroup === "string") {
    return schoolGroup;
  }

  if (isRecord(schoolGroup) && typeof schoolGroup._id === "string") {
    return schoolGroup._id;
  }

  return undefined;
};

const findSchoolGroupById = (id: string): unknown => {
  if (!id) {
    return undefined;
  }

  const activeGroup = schoolGroupStore.getCurrentSchoolGroup;
  if (isRecord(activeGroup) && activeGroup._id === id) {
    return activeGroup;
  }

  const fromStore = schoolGroupStore.getSchoolGroups.find(
    (group) => group._id === id
  );
  if (fromStore) {
    return fromStore;
  }

  const fromFilters = filterStore.filterOptions?.schoolGroups?.find(
    (group) => group._id === id
  );
  if (fromFilters) {
    return fromFilters;
  }

  const userGroups = userStore.user?.schoolGroups;
  if (Array.isArray(userGroups)) {
    for (const group of userGroups) {
      if (isRecord(group) && group._id === id) {
        return group;
      }
    }
  }

  const primaryGroup = userStore.user?.schoolGroup;
  if (isRecord(primaryGroup) && typeof primaryGroup._id === "string" && primaryGroup._id === id) {
    return primaryGroup;
  }

  return undefined;
};

const canUseFreshchat = computed(() => {
  const user = userStore.user;

  if (!user) {
    return false;
  }

  const schoolGroupIdsFromSchools = new Set<string>();
  let explicitlyAllowed = false;

  const schoolCandidates: unknown[] = [
    filterStore.selectedSchool,
    schoolStore.getCurrentSchool,
    user.school,
  ];

  for (const candidate of schoolCandidates) {
    const { value, mode } = getFreshchatConfig(candidate);
    if (typeof value === "boolean") {
      if (value === false) {
        return false;
      }

      explicitlyAllowed = true;
      continue;
    }

    if (mode === "group") {
      const derivedGroupId = getSchoolGroupId(candidate);
      if (derivedGroupId) {
        schoolGroupIdsFromSchools.add(derivedGroupId);
      }
    }
  }

  const schoolGroupCandidates: unknown[] = [];
  const seenGroupIds = new Set<string>();

  const addGroupCandidate = (candidate: unknown) => {
    if (!candidate) {
      return;
    }

    if (typeof candidate === "string") {
      if (seenGroupIds.has(candidate)) {
        return;
      }

      const resolved = findSchoolGroupById(candidate);
      if (resolved && isRecord(resolved)) {
        seenGroupIds.add(candidate);
        schoolGroupCandidates.push(resolved);
      }
      return;
    }

    if (isRecord(candidate)) {
      const candidateId = typeof candidate._id === "string" ? candidate._id : undefined;

      if (candidateId) {
        if (seenGroupIds.has(candidateId)) {
          return;
        }

        seenGroupIds.add(candidateId);
      }

      schoolGroupCandidates.push(candidate);
      return;
    }
  };

  addGroupCandidate(filterStore.selectedSchoolGroup);
  addGroupCandidate(filterStore.selectedFilters.schoolGroup);
  addGroupCandidate(schoolGroupStore.getCurrentSchoolGroup);
  addGroupCandidate(user.schoolGroup);

  for (const groupId of schoolGroupIdsFromSchools) {
    addGroupCandidate(groupId);
  }

  for (const candidate of schoolGroupCandidates) {
    const { value } = getFreshchatConfig(candidate);
    if (typeof value === "boolean") {
      if (value === false) {
        return false;
      }

      explicitlyAllowed = true;
    }
  }

  return explicitlyAllowed;
});

const syncFreshchatUser = () => {
  if (!canUseFreshchat.value) {
    identifyFreshchatUser(null);
    return;
  }

  const user = userStore.user;
  if (!user) {
    identifyFreshchatUser(null);
    return;
  }

  const deriveSchoolGroupName = (): string | undefined => {
    if (filterStore.selectedSchoolGroup?.name) {
      return filterStore.selectedSchoolGroup.name;
    }

    const userSchoolGroups = user.schoolGroups;

    if (Array.isArray(userSchoolGroups) && userSchoolGroups.length > 0) {
      const [firstGroup] = userSchoolGroups;

      if (typeof firstGroup === "string") {
        return firstGroup;
      }

      if (firstGroup && typeof firstGroup === "object" && "name" in firstGroup) {
        return (firstGroup as { name?: string }).name;
      }
    }

    if (typeof user.schoolGroup === "string") {
      return user.schoolGroup;
    }

    return undefined;
  };

  const deriveSchoolName = (): string | undefined => {
    if (filterStore.selectedSchool?.name) {
      return filterStore.selectedSchool.name;
    }

    if (user.school && typeof user.school === "object" && "name" in user.school) {
      return (user.school as { name?: string }).name;
    }

    return undefined;
  };

  identifyFreshchatUser({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    schoolGroup: deriveSchoolGroupName(),
    school: deriveSchoolName(),
    externalId: user._id,
    restoreId: userStore.freshchatRestoreId ?? undefined,
  });
};

onMounted(async () => {
  // Catches an idle tab whose admin has been moved to another region's
  // cluster; the API interceptor catches an active one.
  startRegionMovedWatch();

  // Subjects and products need a signed-in admin, so they load on sign-in
  // (including a session Firebase restores), not only at mount.
  loadOnSignIn(auth, async () => {
    await subjectStore.fetchSubjects();
    await productStore.fetchProducts();
  });
  await schoolStore.fetchLocales();

  schoolStore.setLocale();

  console.log("App Mounted");
});

watchEffect(() => {
  console.log("Freshchat watchEffect triggered. Can use:", canUseFreshchat.value);
  syncFreshchatUser();
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(OutageBannerComponent),
    (_unref(dataStore).loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _hoisted_3,
            _createElementVNode("h3", _hoisted_4, _toDisplayString(_unref(dataStore).loadingMessage), 1)
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_router_view),
    _createVNode(_unref(toastContainers), { position: "bottom-right" })
  ], 64))
}
}

})